import { useCallback, useEffect, useState } from 'react';

export const THEME_KEY = 'assettrack-theme';

// Browser-chrome colour per theme, so the address bar and native controls match
// rather than framing a dark page in white.
const THEME_COLOR = { light: '#FFFFFF', dark: '#0A0A0A' };

function systemTheme() {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

// A choice the user has actually made, as opposed to one inferred from the OS.
// Wrapped because localStorage throws outright in some privacy modes rather
// than returning null.
export function storedTheme() {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === 'dark' || v === 'light' ? v : null;
  } catch {
    return null;
  }
}

function apply(theme) {
  document.documentElement.setAttribute('data-theme', theme);
  document.querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', THEME_COLOR[theme]);
}

// Light or dark, with the choice remembered per browser.
//
// The initial value comes from the attribute the inline script in index.html has
// already set, so React agrees with what is on screen. Doing the first resolve
// here instead would repaint after mount — a white flash on every load for
// anyone using dark.
//
// Nothing is written to storage until the toggle is pressed. That distinction is
// what lets the app keep following the OS for everyone who has never expressed a
// preference: persisting the resolved value on mount would silently freeze the
// first guess in place forever.
export default function useTheme() {
  const [theme, setTheme] = useState(
    () => document.documentElement.getAttribute('data-theme') || storedTheme() || systemTheme()
  );

  useEffect(() => { apply(theme); }, [theme]);

  useEffect(() => {
    if (storedTheme()) return;   // an explicit choice outranks the OS
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq?.addEventListener) return;
    const onChange = e => setTheme(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const toggle = useCallback(() => {
    setTheme(current => {
      const next = current === 'dark' ? 'light' : 'dark';
      try { localStorage.setItem(THEME_KEY, next); } catch { /* private mode — session only */ }
      return next;
    });
  }, []);

  return { theme, toggle };
}
